import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const HEARTBEAT_INTERVAL_MS = 20_000; // 20s when foregrounded

/**
 * Hook that keeps a driver "online" by pinging their geolocation every 20s.
 * Returns { online, lastLocation, toggleOnline }.
 *
 * Feb 2026 rewrite — fixes the "driver flickers online/offline on iOS PWA"
 * bug reported by Beejay. Previously:
 *   - `beforeunload` fired on every screen-lock / app-switch and aggressively
 *     POST-ed /drivers/me/offline, immediately marking him offline.
 *   - Component unmount (any nav, even within the driver app, plus React
 *     StrictMode double-mount in dev) also fired offline.
 *   - `setInterval` silently stopped firing when iOS suspended the PWA in
 *     the background, so when the OS DID wake the timer up enough to fire
 *     one beat, it was already past the 120s stale window.
 *
 * New behaviour:
 *   - The ONLY way a driver goes offline is by tapping the toggle button.
 *     No auto-offline on background, lock screen, app switch, or nav.
 *   - When the PWA returns to the foreground (`visibilitychange` →
 *     visible), fire an IMMEDIATE heartbeat so the operator's map snaps
 *     the driver back to "online" without waiting for the 20s tick.
 *   - Backend stale window is now 300s (5min) — see DRIVER_STALE_SECONDS
 *     in server.py — which is realistic for iOS-PWA timer throttling.
 */
export function useDriverHeartbeat(isDriver) {
  const [online, setOnline] = useState(false);
  const [lastLocation, setLastLocation] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const intervalRef = useRef(null);
  const hasPermission = useRef(false);

  // Auto-enable for drivers once we know they're a driver (auth may load async)
  useEffect(() => {
    if (isDriver) setEnabled(true);
  }, [isDriver]);

  const sendHeartbeat = useCallback(() => {
    if (!('geolocation' in navigator)) {
      toast.error('Geolocation not supported by this device');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        try {
          await axios.post(
            `${API_URL}/api/drivers/me/heartbeat`,
            { latitude: lat, longitude: lng },
            { withCredentials: true }
          );
          setOnline(true);
          setLastLocation({ lat, lng, time: new Date() });
          hasPermission.current = true;
        } catch (err) {
          // Silent fail — heartbeat retries every 20s anyway, and the
          // operator's stale window now tolerates a few missed beats.
        }
      },
      (error) => {
        if (!hasPermission.current) {
          toast.error('Location permission denied — cannot go online', {
            description: 'Enable location access in your browser to go online.'
          });
          setEnabled(false);
        }
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 0 }
    );
  }, []);

  const goOffline = useCallback(async () => {
    try {
      await axios.post(
        `${API_URL}/api/drivers/me/offline`,
        {},
        { withCredentials: true }
      );
    } catch (err) {
      // ignore — driver toggled OFF locally regardless
    }
    setOnline(false);
  }, []);

  const toggleOnline = useCallback(() => {
    if (enabled) {
      setEnabled(false);
      goOffline();
      toast.info('You are now OFFLINE');
    } else {
      setEnabled(true);
      toast.success('Going ONLINE...');
    }
  }, [enabled, goOffline]);

  // Start / stop heartbeat loop
  useEffect(() => {
    if (!isDriver || !enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return undefined;
    }
    // Immediate ping then interval
    sendHeartbeat();
    intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
    return () => {
      // ONLY clear the interval — do NOT POST offline on unmount.
      // Unmounts happen during React re-renders, page navigations, and
      // StrictMode double-mounts; sending offline on each of those was
      // the root cause of Beejay's "flickering offline" bug.
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isDriver, enabled, sendHeartbeat]);

  // Foreground recovery — iOS suspends JS timers when the PWA is
  // backgrounded; when the user brings it forward we may have missed
  // several intervals. Send an IMMEDIATE heartbeat the moment we're
  // visible again so the operator's map snaps us back to online.
  useEffect(() => {
    if (!isDriver || !enabled) return undefined;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        sendHeartbeat();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    // pageshow fires when the PWA returns from BFCache (iOS app switch)
    window.addEventListener('pageshow', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onVisibility);
    };
  }, [isDriver, enabled, sendHeartbeat]);

  return { online, lastLocation, enabled, toggleOnline };
}
